import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';

export default function BeginnerTutorial() {
  return (
    <>
      <JsonLd
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: 'Sugargoo Beginner Tutorial 2026: First-Time User Guide',
          description: 'Step-by-step Sugargoo beginner tutorial covering account setup, first order, warehouse process, shipping, and essential tips for first-time international shoppers.',
          datePublished: '2026-04-01',
          dateModified: '2026-08-06',
          author: { '@type': 'Organization', name: 'Sugargoo Sheet Help' },
          publisher: { '@type': 'Organization', name: 'Sugargoo Sheet Help' },
          mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://sugargoosheet.help/sugargoo-beginner-tutorial' },
        }}
      />
      <JsonLd
        schema={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://sugargoosheet.help/' },
            { '@type': 'ListItem', position: 2, name: 'Sugargoo Beginner Tutorial 2026', item: 'https://sugargoosheet.help/sugargoo-beginner-tutorial' },
          ],
        }}
      />
      <JsonLd
        schema={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            { '@type': 'Question', name: 'Is Sugargoo safe for first-time users?', acceptedAnswer: { '@type': 'Answer', text: 'Yes, Sugargoo is a legitimate platform used by thousands of shoppers. Start with small orders and use community resources like the Spreadsheet to minimize risk.' } },
            { '@type': 'Question', name: 'How long does my first order take from start to finish?', acceptedAnswer: { '@type': 'Answer', text: 'From placing your order to receiving the package typically takes 3-6 weeks total including domestic delivery, QC, consolidation, and international shipping.' } },
            { '@type': 'Question', name: 'Do I need to speak Chinese to use Sugargoo?', acceptedAnswer: { '@type': 'Answer', text: 'No, the Sugargoo platform is fully available in English. Browser translation tools and curated resources like the Spreadsheet help overcome language barriers.' } },
            { '@type': 'Question', name: 'What payment methods does Sugargoo accept?', acceptedAnswer: { '@type': 'Answer', text: 'Sugargoo accepts various payment methods including PayPal, credit cards, and other international payment options.' } },
            { '@type': 'Question', name: 'Can I cancel an order after placing it?', acceptedAnswer: { '@type': 'Answer', text: 'Orders can usually be canceled before the seller ships the item. Once shipped to the warehouse, cancellation may not be possible.' } },
          ],
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <article className="pt-24 md:pt-28 pb-16 md:pb-20">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <nav className="flex items-center gap-1.5 text-xs text-foreground-500 font-label mb-6 flex-wrap">
                <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
                <i className="ri-arrow-right-s-line text-[10px]"></i>
                <span className="text-foreground-700">Sugargoo Beginner Tutorial</span>
              </nav>

              <div className="mb-8">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-label font-medium">Beginner</span>
                  <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-label font-medium">2026 Edition</span>
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                  Sugargoo Beginner Tutorial 2026: First-Time User Guide
                </h1>
                <p className="text-lg text-foreground-600 leading-relaxed max-w-3xl">
                  New to Sugargoo? This step-by-step tutorial walks you through everything from account creation to receiving your first package. By the end of this guide, you will be ready to shop confidently and avoid common first-timer mistakes.
                </p>
              </div>

              <div className="aspect-[21/9] w-full rounded-2xl overflow-hidden bg-background-200 mb-10">
                <img
                  src="https://readdy.ai/api/search-image?query=Person%20unboxing%20a%20cardboard%20package%20on%20a%20warm%20wooden%20desk%2C%20multiple%20small%20wrapped%20items%20inside%2C%20soft%20natural%20lighting%20from%20a%20window%2C%20cream%20neutral%20background%2C%20editorial%20lifestyle%20photography%20with%20clean%20minimal%20aesthetic%20and%20warm%20earth%20tones&width=1200&height=514&seq=beginner-hero-2026&orientation=landscape"
                  alt="Sugargoo Beginner Tutorial 2026 - first-time user guide and unboxing"
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="flex flex-col lg:flex-row gap-8 md:gap-12">
                <div className="flex-1">
                  <div className="prose-custom">
                    <h2>What Is Sugargoo? A Quick Overview</h2>
                    <p>
                      Sugargoo is a <strong>shopping agent platform</strong> that allows international customers to buy products from Chinese e-commerce websites like Taobao, Weidian, and 1688. Think of Sugargoo as your personal shopper in China. They purchase items on your behalf, receive them at their warehouse, inspect and photograph them, and then ship everything to your international address in a consolidated package.
                    </p>
                    <p>
                      This service solves the biggest problem for international shoppers: Chinese sellers typically do not ship overseas. Sugargoo acts as the bridge, handling domestic purchasing, warehousing, consolidation, and international logistics so you can access products that would otherwise be unavailable in your country.
                    </p>

                    <h2>Step 1: Creating Your Sugargoo Account</h2>
                    <p>
                      Getting started is straightforward. Visit the Sugargoo website and register with your email address. Verify your email to activate the account. Complete your profile with your international shipping address, which will be saved for future orders. Familiarize yourself with the dashboard layout, which shows your balance, active orders, warehouse inventory, and shipping options.
                    </p>

                    <h2>Step 2: Finding Products to Buy</h2>
                    <p>
                      There are two ways to find products: browsing Chinese marketplaces directly and pasting links into Sugargoo, or using curated resources like the <strong>Sugargoo Spreadsheet</strong> to discover community-verified finds. For beginners, we strongly recommend starting with the Spreadsheet database, which organizes products by category with community feedback, quality notes, and direct purchase links. This dramatically reduces the risk of disappointing purchases.
                    </p>

                    <div className="cta-card">
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                        <div className="flex-1">
                          <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground-900 mb-2">Start with Verified Finds</h3>
                          <p className="text-sm text-foreground-600">The Sugargoo Spreadsheet is the best starting point for beginners. Browse community-tested products organized by category.</p>
                        </div>
                        <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-label font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap flex-shrink-0"
                        >
                          Sugargoo Spreadsheet
                          <i className="ri-arrow-right-up-line"></i>
                        </a>
                      </div>
                    </div>

                    <h2>Step 3: Placing Your First Order</h2>
                    <p>
                      Once you have found a product link, paste it into the Sugargoo order submission form. Select the correct size, color, and any other options exactly as shown on the original listing. Double-check your selections before submitting. Sugargoo will then purchase the item from the seller on your behalf and update the order status in your dashboard.
                    </p>
                    <p>
                      Your account balance must cover the item cost plus domestic shipping to the warehouse. Top up your balance using the available payment methods. For your first order, consider starting with a single affordable item to learn the process before committing to larger purchases.
                    </p>

                    <h2>Step 4: Understanding the Warehouse Process</h2>
                    <p>
                      After your order arrives at the Sugargoo warehouse, staff will photograph it from multiple angles and upload <strong>QC (Quality Control) photos</strong> to your account. Review these photos carefully to verify quality, sizing, and color accuracy. If the item meets your expectations, approve it for consolidation. If not, you can request a return to the seller.
                    </p>
                    <p>
                      Warehouse storage is free for 90 days, giving you plenty of time to place multiple orders and consolidate everything into a single shipment. This consolidation is one of the key advantages of using an agent &mdash; combining multiple items into one parcel significantly reduces per-item shipping costs.
                    </p>

                    <h2>Step 5: Submitting Your Parcel for Shipping</h2>
                    <p>
                      When you are ready to ship, select the items you want to include in your parcel. Choose your preferred shipping line based on speed, cost, and your destination. Decide on packaging preferences, such as whether to remove shoe boxes or tags. Select insurance if desired, especially for valuable orders. Review the shipping cost estimate and submit your parcel.
                    </p>

                    <h2>Step 6: Tracking and Receiving Your Package</h2>
                    <p>
                      After your parcel ships, you will receive a tracking number. Use this to monitor your package's journey through the shipping carrier's website. Delivery times vary by shipping line and destination, ranging from 5 days for express to 40+ days for economy services. Once your package arrives, inspect everything immediately and report any issues to Sugargoo support within their specified timeframe.
                    </p>

                    <h2>Essential Tips for First-Time Users</h2>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
                      {[
                        { title: 'Start Small', desc: 'Place one small order first to learn the process before larger purchases.' },
                        { title: 'Check Size Charts', desc: 'Asian sizing runs smaller. Always measure and compare to charts.' },
                        { title: 'Read Reviews', desc: 'Community feedback in the Spreadsheet helps identify quality sellers.' },
                        { title: 'Be Patient', desc: 'Shipping takes time. Economy lines can take 20-40 days.' },
                        { title: 'Review QC Photos', desc: 'Always check warehouse photos before approving shipping.' },
                        { title: 'Budget for Shipping', desc: 'Shipping often costs as much as or more than the products themselves.' },
                      ].map((tip, i) => (
                        <div key={i} className="bg-background-100 border border-background-200/70 rounded-xl p-5">
                          <h4 className="font-heading text-base font-semibold text-foreground-900 mb-1">{tip.title}</h4>
                          <p className="text-sm text-foreground-600">{tip.desc}</p>
                        </div>
                      ))}
                    </div>

                    <h2>Common Beginner Mistakes to Avoid</h2>
                    <p>
                      New Sugargoo users often make predictable mistakes that are easy to avoid with a little preparation. The most common errors include: ignoring size charts and assuming Western sizing; not budgeting adequately for international shipping; failing to review QC photos carefully before approving; ordering from unverified sellers without community feedback; and not understanding customs duties in their destination country. Avoiding these mistakes will make your first experience significantly smoother and more satisfying.
                    </p>

                    <h2>How Much Should I Budget for My First Order?</h2>
                    <p>
                      For a realistic first order, budget approximately <strong>$50-100 in total</strong>. This covers product costs, domestic shipping to the warehouse, international shipping to your address, and a small buffer for unexpected fees. A typical first order might include 1-2 clothing items or a single pair of shoes. As you gain experience and understand shipping costs better, you can scale up to larger, more cost-efficient consolidated orders.
                    </p>

                    <h2>Beginner FAQ</h2>

                    <div className="space-y-4 my-6">
                      {[
                        {
                          q: 'Is Sugargoo safe for first-time users?',
                          a: 'Yes, Sugargoo is a legitimate platform used by thousands of shoppers. Start with small orders and use community resources like the Spreadsheet to minimize risk.',
                        },
                        {
                          q: 'How long does my first order take from start to finish?',
                          a: 'From placing your order to receiving the package typically takes 3-6 weeks total. This includes 1-2 weeks for domestic delivery to the warehouse, 1 week for QC and consolidation, and 1-3 weeks for international shipping.',
                        },
                        {
                          q: 'Do I need to speak Chinese to use Sugargoo?',
                          a: 'No, the Sugargoo platform is fully available in English. Product listings on Chinese marketplaces may be in Chinese, but browser translation tools and curated resources like the Spreadsheet help overcome this barrier.',
                        },
                        {
                          q: 'What payment methods does Sugargoo accept?',
                          a: 'Sugargoo accepts various payment methods including PayPal, credit cards, and other international payment options. Your account balance is used to pay for orders and shipping.',
                        },
                        {
                          q: 'Can I cancel an order after placing it?',
                          a: 'Orders can usually be canceled before the seller ships the item. Once shipped to the warehouse, cancellation may not be possible. Contact customer support immediately if you need to cancel.',
                        },
                      ].map((faq, i) => (
                        <div key={i} className="bg-background-100 border border-background-200/70 rounded-xl p-5">
                          <h4 className="font-heading text-base font-semibold text-foreground-900 mb-2">{faq.q}</h4>
                          <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-10 p-6 md:p-8 bg-background-100 border border-background-200/70 rounded-2xl">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                      <div className="flex-1">
                        <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground-900 mb-2">Ready to Start Shopping?</h3>
                        <p className="text-sm text-foreground-600">Browse verified finds and place your first order with confidence using the Sugargoo Spreadsheet.</p>
                      </div>
                      <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-label font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap flex-shrink-0"
                      >
                        Buy It Now
                        <i className="ri-arrow-right-up-line"></i>
                      </a>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-background-200/70 flex flex-wrap gap-4">
                    <Link to="/" className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-label font-medium transition-colors"
                    >
                      <i className="ri-arrow-left-line"></i>Home
                    </Link>
                    <Link to="/sugargoo-spreadsheet-guide" className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-label font-medium transition-colors"
                    >
                      Spreadsheet Guide
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>

                <aside className="lg:w-72 flex-shrink-0">
                  <div className="sticky top-24 space-y-6">
                    <div className="bg-background-100 border border-background-200/70 rounded-xl p-5">
                      <h4 className="font-label text-sm font-semibold text-foreground-900 mb-3 uppercase tracking-wider">Quick Links</h4>
                      <div className="space-y-2.5">
                        <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-label font-medium transition-colors"
                        >
                          <i className="ri-external-link-line"></i>Sugargoo Spreadsheet
                        </a>
                        <Link to="/" className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-label font-medium transition-colors"
                        >
                          <i className="ri-home-line"></i>Homepage
                        </Link>
                      </div>
                    </div>
                    <div className="bg-accent-100 border border-accent-200/50 rounded-xl p-5">
                      <h4 className="font-label text-sm font-semibold text-accent-800 mb-3 uppercase tracking-wider">Related Guides</h4>
                      <div className="space-y-2.5">
                        <Link to="/sugargoo-spreadsheet-guide" className="block text-sm text-accent-700 hover:text-accent-800 transition-colors">Spreadsheet Guide</Link>
                        <Link to="/sugargoo-shipping-cost-guide" className="block text-sm text-accent-700 hover:text-accent-800 transition-colors">Shipping Cost Guide</Link>
                        <Link to="/sugargoo-warehouse-guide" className="block text-sm text-accent-700 hover:text-accent-800 transition-colors">Warehouse Guide</Link>
                        <Link to="/sugargoo-review-2026" className="block text-sm text-accent-700 hover:text-accent-800 transition-colors">Sugargoo Review</Link>
                      </div>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}